import { type Provider, type ProviderConfiguration, type EmptyProviderConfiguration, type Integration } from '@/models/configuration/ProviderConfiguration';
import { HttpError, getJwt } from '../common';
import { createApiResource } from '../adminApi';

const BASE_URL = import.meta.env.VITE_API_URL;

export const providerConfigurationApi = {
    ...createApiResource<ProviderConfiguration, string>('configuration/providerconfiguration'),
    list: undefined,
    listProviders: async (kind: number): Promise<Provider[]> => {
        const response = await fetch(`${BASE_URL}/configuration/providerconfiguration/providers?kind=${kind}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as Provider[];
    },
    // The configurable integrations a vendor exposes (+ the concept each serves). Payment-only today; other
    // kinds return []. Lets the payment editor offer an integration picker (e.g. hosted-payment-page vs afcc).
    listIntegrations: async (kind: number, vendor: string): Promise<Integration[]> => {
        const response = await fetch(`${BASE_URL}/configuration/providerconfiguration/integrations?kind=${kind}&vendor=${encodeURIComponent(vendor)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as Integration[];
    },
    get: async (id: string, kind: number, vendor?: string, version?: string, integrationType?: string): Promise<ProviderConfiguration | null> => {
        let url = `${BASE_URL}/configuration/providerconfiguration/${id}?kind=${kind}`;
        if (vendor) url += `&vendor=${vendor}`
        if (version) url += `&version=${version}`
        if (integrationType) url += `&integrationType=${encodeURIComponent(integrationType)}`

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        // A location with no config for this kind (e.g. no loyalty/feedback vendor) returns an empty/null
        // body or a non-200 — treat that as "no config" rather than letting response.json() throw.
        if (!response.ok) return null;
        const text = await response.text();
        return text ? (JSON.parse(text) as ProviderConfiguration) : null;
    },
    getEmpty: async (kind: number, vendor: string, version: string, integrationType?: string): Promise<EmptyProviderConfiguration> => {
        let url = `${BASE_URL}/configuration/providerconfiguration/empty?kind=${kind}&vendor=${vendor}&version=${version}`;
        if (integrationType) url += `&integrationType=${encodeURIComponent(integrationType)}`
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${getJwt()}`
            }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new HttpError(response.status, data);
        return data as EmptyProviderConfiguration;
    },
    create: async (item: ProviderConfiguration): Promise<ProviderConfiguration> => {
        const response = await fetch(`${BASE_URL}/configuration/providerconfiguration/`, {
            method: 'POST',
            body: JSON.stringify(item),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getJwt()}`
            },
        });

        const contentType = response.headers.get('content-type') ?? '';
        const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

        if (!response.ok) throw new HttpError(response.status, data);
        return await data;
    },
    update: async (item: ProviderConfiguration): Promise<void> => {
        const response = await fetch(`${BASE_URL}/configuration/providerconfiguration/${(item as any).locationId}`, {
            method: 'PUT',
            body: JSON.stringify(item),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getJwt()}`
            },
        });

        const contentType = response.headers.get('content-type') ?? '';
        const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

        if (!response.ok) throw new HttpError(response.status, data);
    },
};